import { DateTime } from 'luxon';
import { Link, useNavigate } from 'react-router';
import { useI18nContext } from '@/i18n/i18n-react';
import type { Locales } from '@/i18n/i18n-types';
import type { CampaignDto } from '@/generated/korza-api-client/types.gen';
import { useIsMobile } from '@/hooks/use-mobile';
import { formatDayMonth } from '@/lib/partial-buckets';
import { mobileLayer } from '@/lib/mobile-layer';
import { cn } from '@/lib/utils';
import { formatCurrency, formatNumber } from '@/components/metrics/formatters';
import { HomeCardLink } from '@/components/home/home-card-link';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

/** ANLT-05: up to 10 campaigns of the period. */
export const PERIOD_CAMPAIGNS_LIMIT = 10;

export interface CampaignsPeriodTableProps {
  tenantSlug: string;
  /** Sent in the period, most recent first (backend `launchDateTime desc`). */
  campaigns: CampaignDto[] | undefined;
  currency: string;
}

const DASH = '—';

function percent1(ratio: number, locale: Locales): string {
  return new Intl.NumberFormat(locale, {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(ratio);
}

/** "10 out, 10:00" in the browser's zone. */
function sentAtLabel(iso: string, locale: Locales): string {
  const date = DateTime.fromISO(iso);
  return `${formatDayMonth(date, locale)}, ${date.toFormat('HH:mm')}`;
}

/**
 * "Campanhas do período" (ANLT-05): the 10 most recent campaigns sent in the
 * period with send time, sent, CTR, conversion, orders and revenue (with a
 * bar relative to the largest); a table from 768px, a list below. Each row
 * opens the campaign; "—" when the campaign hides its data.
 */
export function CampaignsPeriodTable({
  tenantSlug,
  campaigns,
  currency,
}: CampaignsPeriodTableProps) {
  const { LL, locale } = useI18nContext();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const base = `/${tenantSlug}/campaigns`;

  const rows = (campaigns ?? [])
    .slice(0, PERIOD_CAMPAIGNS_LIMIT)
    .map((campaign) => {
      const a = campaign.analytics;
      const hidden = a.dataSufficiency.shouldHideData;
      return {
        id: campaign.id,
        name: campaign.name,
        sentAt: sentAtLabel(campaign.launchDateTime, locale),
        sent: hidden ? DASH : formatNumber(a.notificationsSent, locale),
        ctr: hidden ? DASH : percent1(a.ctr, locale),
        conversion: hidden ? DASH : percent1(a.conversionRate, locale),
        orders: hidden ? DASH : formatNumber(a.paidOrderCount, locale),
        revenue: hidden ? DASH : formatCurrency(a.revenue, locale, currency),
        revenueValue: hidden ? 0 : a.revenue,
      };
    });
  const maxRevenue = Math.max(0, ...rows.map((row) => row.revenueValue));
  const barOf = (value: number) =>
    maxRevenue > 0 ? (value / maxRevenue) * 100 : 0;

  const body = () => {
    if (!campaigns) {
      return (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      );
    }
    if (rows.length === 0) {
      return (
        <p className="py-3 text-sm text-muted-foreground">
          {LL.metricsPage.pushTab.periodCampaigns.empty()}
        </p>
      );
    }
    if (isMobile) {
      return (
        <ul data-period-campaigns-list className="flex flex-col">
          {rows.map((row) => (
            <li key={row.id} className="border-t">
              <Link
                to={`${base}/${row.id}`}
                className={cn('flex flex-col gap-1.5 py-3', mobileLayer.touch)}
              >
                <span className="flex justify-between gap-2.5">
                  <span className="min-w-0 truncate text-sm font-semibold">
                    {row.name}
                  </span>
                  <span className="shrink-0 text-sm font-semibold tabular-nums">
                    {row.revenue}
                  </span>
                </span>
                <span className="text-xs text-muted-foreground">
                  {LL.metricsPage.pushTab.periodCampaigns.listLine({
                    date: row.sentAt,
                    ctr: row.ctr,
                    orders: row.orders,
                  })}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      );
    }
    const num = 'px-3 text-right tabular-nums';
    return (
      <div className="overflow-x-auto">
        <table
          data-period-campaigns-table
          className="w-full min-w-[720px] text-sm"
        >
          <thead>
            <tr className="border-b text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
              <th scope="col" className="px-3 pb-2.5 text-left font-semibold">
                {LL.metricsPage.pushTab.periodCampaigns.campaign()}
              </th>
              <th scope="col" className="px-3 pb-2.5 text-left font-semibold">
                {LL.metricsPage.pushTab.periodCampaigns.sentAt()}
              </th>
              <th scope="col" className={cn(num, 'pb-2.5 font-semibold')}>
                {LL.metricsPage.pushTab.periodCampaigns.sent()}
              </th>
              <th scope="col" className={cn(num, 'pb-2.5 font-semibold')}>
                {LL.metricsPage.pushTab.periodCampaigns.ctr()}
              </th>
              <th scope="col" className={cn(num, 'pb-2.5 font-semibold')}>
                {LL.metricsPage.pushTab.periodCampaigns.conversion()}
              </th>
              <th scope="col" className={cn(num, 'pb-2.5 font-semibold')}>
                {LL.metricsPage.pushTab.periodCampaigns.orders()}
              </th>
              <th scope="col" className={cn(num, 'pb-2.5 font-semibold')}>
                {LL.metricsPage.pushTab.periodCampaigns.revenue()}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.id}
                data-campaign-row
                className="cursor-pointer border-b last:border-b-0 hover:bg-muted/50"
                onClick={() => navigate(`${base}/${row.id}`)}
              >
                <td className="px-3 py-3 font-semibold">
                  <Link
                    to={`${base}/${row.id}`}
                    className="hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    onClick={(event) => event.stopPropagation()}
                  >
                    {row.name}
                  </Link>
                </td>
                <td className="whitespace-nowrap px-3 py-3 text-muted-foreground">
                  {row.sentAt}
                </td>
                <td className={cn(num, 'py-3')}>{row.sent}</td>
                <td className={cn(num, 'py-3')}>{row.ctr}</td>
                <td className={cn(num, 'py-3')}>{row.conversion}</td>
                <td className={cn(num, 'py-3')}>{row.orders}</td>
                <td className={cn(num, 'py-3')}>
                  <span className="inline-flex items-center gap-2.5 font-semibold">
                    <span
                      aria-hidden="true"
                      className="h-1 w-16 overflow-hidden rounded-sm bg-muted"
                    >
                      <span
                        data-revenue-bar
                        className="block h-full bg-[#155DFC]"
                        style={{ width: `${barOf(row.revenueValue)}%` }}
                      />
                    </span>
                    {row.revenue}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <Card
      data-period-campaigns
      className="flex flex-col gap-2.5 px-5 pb-2 pt-5 max-md:px-4"
    >
      <div className="flex flex-wrap items-center justify-between gap-x-2.5 gap-y-1">
        <h3 className="text-sm font-semibold">
          {LL.metricsPage.pushTab.periodCampaigns.title()}
        </h3>
        <HomeCardLink to={`${base}?view=list`}>
          {LL.metricsPage.campaignPerformance.viewResultsByCampaign()}
        </HomeCardLink>
      </div>
      {body()}
    </Card>
  );
}
